import {
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  Button,
  DocumentPreviewView,
  HelpTip,
  Icon,
  PdfViewer,
  cn,
  errorMessage,
} from '@dataroom/shared';
import { invalidateRoomDocuments, useRoomDocuments } from '@/api/hooks';
import { useRedactedCopy, viewerPlan } from '@dataroom/shared';
import { useDocumentPreview } from '@dataroom/shared';
import {
  addRedactionBox,
  applyRedaction,
  approvalLine,
  approveRedaction,
  boxFromDrag,
  decideRedactionMark,
  detectRedaction,
  fetchDocumentRedaction,
  fetchRedactionReviewList,
  markKind,
  markState,
  marksByPage,
  maskedPreview,
  nextNeedingReview,
  redactionQueryKey,
  redactionReviewListKey,
  redactionReviewPath,
  reviewPhase,
  runProgress,
  stopReason,
  stoppedForFile,
  undecidedCount,
  viewerRedactionStatus,
  type DocumentRedaction,
  type RedactionBox,
  type RedactionMarkView,
  type ReviewPhase,
} from '@/lib/redaction-review';
import { useRoomContext } from './context';

/**
 * REVIEW REDACTION (owner, 2026-10-15: "When it says 'Redaction needs review',
 * that's nice, but there is nowhere to review the redaction"). One document at
 * a time, `/rooms/:roomId/redaction?doc=<id>`:
 *
 *   - the original, page by page, with every mark drawn where it sits — black
 *     = will be hidden, striped = waiting for a decision, dashed = kept visible;
 *   - why the last attempt stopped, in plain words (the run's content-free
 *     reason and page);
 *   - the marks by page — what each is (never its value: at most the last four
 *     digits of the masked preview) — with Hide / Keep visible, "Hide all on
 *     this page", and a click that scrolls to the mark;
 *   - draw a box (drag on the page) over anything detection missed; remove a
 *     box you drew;
 *   - "Apply and make redacted copy" once nothing is undecided → the copy is made
 *     and VERIFIED on the worker (fails closed); success shows the redacted copy,
 *     a failure shows why and keeps the person here;
 *   - "Run detection again" (a run that stopped can only go forward that way —
 *     the server refuses Apply; decisions carry over);
 *   - "Redaction is right — approve" (owner, 2026-10-16: "How does one certify
 *     the redaction as good?"): records who approved and when, hides any mark
 *     still undecided (the marks as shown), makes or keeps the redacted copy, and
 *     moves on to the next document.
 *
 * The page says only what is true for THIS file: drawing hints only where the
 * page can be drawn on (a PDF or an image — a Word / Excel preview cannot),
 * the list hint only when there are marks, and why a run stopped in one
 * sentence that names the reason that applies.
 *
 * UI over the existing, permission-checked review API (ADR-0057): nothing here
 * decides who sees what.
 */
export function RoomRedactionReviewPage() {
  const { roomId } = useRoomContext();
  const [search] = useSearchParams();
  const documentId = search.get('doc');
  if (!documentId) return <Navigate to={`/rooms/${roomId}/processing`} replace />;
  return <RedactionReview key={documentId} roomId={roomId} documentId={documentId} />;
}

/** How often a working run is re-read. */
const WORKING_POLL_MS = 1500;

type Viewed = 'original' | 'buyer';

function RedactionReview({ roomId, documentId }: { roomId: string; documentId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const documents = useRoomDocuments(roomId);
  const doc = documents.data?.find((d) => d.id === documentId);
  const key = redactionQueryKey(roomId, documentId);
  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchDocumentRedaction(roomId, documentId),
    retry: false,
    refetchInterval: (q) => (reviewPhase(q.state.data) === 'working' ? WORKING_POLL_MS : false),
  });
  const data = query.data;
  const phase = reviewPhase(data);
  const marks = data?.marks ?? [];
  const pending = undecidedCount(marks);

  const [viewed, setViewed] = useState<Viewed>('original');
  const [selected, setSelected] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** The last Apply / detection this person asked for — its outcome is announced. */
  const [requested, setRequested] = useState<'apply' | 'detect' | null>(null);
  const [drawTouch, setDrawTouch] = useState(false);
  // A touch screen or pen, at ANY width (a touch laptop is wide): its drag
  // scrolls the page unless drawing with touch / pen is turned on.
  const [touchScreen, setTouchScreen] = useState(
    () => typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 0,
  );
  /** What the original is shown as: a page that can be drawn on, or a Word / Excel preview. */
  const [surface, setSurface] = useState<Surface>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const made = requested !== null && phase === 'done';
  // The copy was made: show it (owner: "show the redacted copy").
  useEffect(() => {
    if (made) setViewed('buyer');
  }, [made]);

  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const patchCache = (fn: (d: DocumentRedaction) => DocumentRedaction) =>
    qc.setQueryData<DocumentRedaction>(key, (d) => (d ? fn(d) : d));
  const fail = (err: unknown, fallback: string) => setActionError(errorMessage(err) ?? fallback);

  const decide = useMutation({
    mutationFn: async (items: { markId: string; decision: 'approved' | 'rejected' }[]) => {
      // One at a time: each decision is its own audited write.
      for (const it of items) {
        await decideRedactionMark(roomId, it.markId, it.decision);
        patchCache((d) => ({
          ...d,
          run: d.run ? { ...d.run, changesPending: true } : d.run,
          marks: d.marks.map((m) => (m.markId === it.markId ? { ...m, decision: it.decision } : m)),
        }));
      }
    },
    onMutate: () => setActionError(null),
    onError: (err) => fail(err, 'That decision could not be saved.'),
    onSettled: () => {
      void refresh();
      // The Processing list reads the same state ("Needs you: review redaction").
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'processing'] });
    },
  });

  const draw = useMutation({
    mutationFn: (v: { pageNumber: number; box: RedactionBox }) =>
      addRedactionBox(roomId, documentId, v.pageNumber, v.box),
    onMutate: () => setActionError(null),
    onSuccess: (r) => setSelected(r.markId),
    onError: (err) => fail(err, 'The box could not be added.'),
    onSettled: () => void refresh(),
  });

  /** Apply / detect: 202, then the screen polls the run until it settles. */
  const start = useMutation({
    mutationFn: (kind: 'apply' | 'detect') =>
      kind === 'apply' ? applyRedaction(roomId, documentId) : detectRedaction(roomId, documentId),
    onMutate: () => {
      setActionError(null);
      setRequested(null);
    },
    onSuccess: (_r, kind) => {
      setRequested(kind);
      setViewed('original');
      // Working from now until the next read says otherwise.
      patchCache((d) => ({
        ...d,
        run: d.run ? { ...d.run, status: 'queued', phase: kind, pagesDone: 0 } : d.run,
      }));
      void refresh();
      // The room's lists change once this settles; refetch them then.
      void qc.invalidateQueries({ queryKey: redactionReviewListKey(roomId) });
      // A started run takes the document out of "Needs your attention" now.
      void invalidateRoomDocuments(qc, roomId);
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.problem?.code : undefined;
      if (code === 'redaction_needs_redetect') {
        setActionError(
          'This copy could not be made safely last time, so it can’t simply be applied — choose Run detection again (your decisions are kept).',
        );
      } else {
        fail(err, 'That could not be started.');
      }
      void refresh();
    },
  });

  // Listings elsewhere read the document's redaction state: refresh them once a run settles.
  const settled = phase !== 'working';
  useEffect(() => {
    if (settled && requested) {
      void invalidateRoomDocuments(qc, roomId);
    }
  }, [settled, requested, qc, roomId]);

  const [walking, setWalking] = useState(false);
  /** The next document still needing review — fresh from the server — or back to the list. */
  const goNext = async () => {
    setWalking(true);
    try {
      const list = await qc.fetchQuery({
        queryKey: redactionReviewListKey(roomId),
        queryFn: () => fetchRedactionReviewList(roomId),
        staleTime: 0,
      });
      const next = nextNeedingReview(list, documentId);
      if (next) navigate(redactionReviewPath(roomId, next.id));
      else navigate(`/rooms/${roomId}/processing`, { state: { redactionsReviewed: true } });
    } catch (err) {
      fail(err, 'The next document could not be found.');
      setWalking(false);
    }
  };

  /** "Redaction is right — approve": recorded, then on to the next document. */
  const approve = useMutation({
    mutationFn: () => approveRedaction(roomId, documentId),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      void refresh();
      void qc.invalidateQueries({ queryKey: redactionReviewListKey(roomId) });
      void invalidateRoomDocuments(qc, roomId);
      void goNext();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.problem?.code : undefined;
      setActionError(
        code === 'redaction_needs_redetect'
          ? 'No redacted copy can be made from the last attempt, so there is nothing to approve yet.'
          : (errorMessage(err) ?? 'The approval could not be saved.'),
      );
      void refresh();
    },
  });

  const busy = decide.isPending || draw.isPending || start.isPending || approve.isPending;
  const editable =
    !!data?.run && phase !== 'working' && phase !== 'unsupported' && phase !== 'none';
  const canApply = phase === 'apply' && !busy;
  const approved = approvalLine(data?.run);
  // A copy exists or can be made from this run: decided or not, found or not.
  const canApprove =
    (phase === 'decide' || phase === 'apply' || (phase === 'done' && !approved)) && !busy;
  const drawable = surface === 'drawable';
  const fileStop = stoppedForFile(data?.run);

  /** Select a mark; from the list, scroll the page to it (and back from the page). */
  const select = (markId: string, from: 'list' | 'page') => {
    setSelected(markId);
    const root = from === 'list' ? viewerRef.current : listRef.current;
    const target = root?.querySelector<HTMLElement>(
      from === 'list' ? `[data-mark-box="${markId}"]` : `[data-mark-item="${markId}"]`,
    );
    if (from === 'list') setViewed('original');
    // Not every browser (or test DOM) scrolls an element into view.
    target?.scrollIntoView?.({ block: from === 'list' ? 'center' : 'nearest', inline: 'nearest' });
  };

  const titleId = useId();
  const listTitleId = useId();

  if (query.isError) {
    return (
      <div className="space-y-3">
        <BackLink roomId={roomId} />
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(query.error) ?? 'The redaction for this document could not be loaded.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" aria-labelledby={titleId} role="region">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <BackLink roomId={roomId} />
          <h1 id={titleId} className="mt-1 text-lg font-semibold">
            Review redaction
          </h1>
          <p className="break-words text-sm text-ink-2">
            {doc?.name ?? (documents.isPending ? 'Loading…' : '')}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void goNext()} disabled={walking}>
          Skip to next document
        </Button>
      </div>

      <StatusBanner
        data={data}
        phase={phase}
        pending={pending}
        loading={query.isPending}
        made={made}
        requested={requested}
        onNext={() => void goNext()}
        walking={walking}
        drawable={drawable}
        fileName={doc?.name ?? null}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <section
          aria-label="The document"
          className="flex h-[70vh] min-h-[420px] flex-col overflow-hidden rounded-lg border bg-background"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
            <div className="flex items-center gap-1.5">
              <ViewSwitch value={viewed} onChange={setViewed} />
              <HelpTip id="admin.redaction.view-switch" />
            </div>
            {viewed === 'original' ? (
              <div className="flex items-center gap-1.5">
                <Legend />
                <HelpTip id="admin.redaction.legend" />
              </div>
            ) : null}
          </div>
          <div ref={viewerRef} className="flex min-h-0 flex-1 flex-col">
            {viewed === 'buyer' ? (
              <BuyerCopy
                roomId={roomId}
                documentId={documentId}
                data={data}
                title={doc?.name ?? 'Document'}
              />
            ) : doc ? (
              <OriginalWithMarks
                roomId={roomId}
                documentId={documentId}
                name={doc.name}
                marks={marks}
                selected={selected}
                onSelect={(id) => select(id, 'page')}
                canDraw={editable && !busy}
                drawTouch={drawTouch}
                onDraw={(pageNumber, box) => draw.mutate({ pageNumber, box })}
                onTouch={() => setTouchScreen(true)}
                onSurface={setSurface}
              />
            ) : (
              <p className="p-6 text-sm text-muted-foreground">
                {documents.isPending
                  ? 'Loading document…'
                  : 'This document is not available to view.'}
              </p>
            )}
          </div>
          {viewed === 'original' && editable && drawable ? (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2 text-xs text-ink-3">
              <span>
                {touchScreen
                  ? 'Missed something? Drag on the page with the mouse — or turn on drawing with touch or pen — to draw a box over it.'
                  : 'Missed something? Drag on the page to draw a box over it.'}
              </span>
              {touchScreen ? (
                <Button
                  variant={drawTouch ? 'secondary' : 'outline'}
                  size="sm"
                  aria-pressed={drawTouch}
                  onClick={() => setDrawTouch((v) => !v)}
                >
                  {drawTouch ? 'Drawing on — tap to stop' : 'Draw with touch or pen'}
                </Button>
              ) : null}
            </div>
          ) : null}
        </section>

        <aside className="space-y-4" aria-label="Marks and actions">
          <div className="space-y-2 rounded-lg border bg-surface p-3">
            {approved && phase === 'done' ? (
              <p className="text-sm font-medium text-st-filed-fg" data-testid="redaction-approved">
                {approved}
              </p>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <Button
                    className="min-w-0 flex-1"
                    disabled={!canApprove}
                    onClick={() => approve.mutate()}
                    aria-describedby={`${titleId}-approve`}
                  >
                    Redaction is right — approve
                  </Button>
                  <HelpTip id="admin.redaction.approve" />
                </div>
                <p id={`${titleId}-approve`} className="text-xs text-ink-3">
                  {approveHint(phase, pending, marks.length)}
                </p>
              </>
            )}
            <Button
              variant="outline"
              className="w-full"
              disabled={!canApply}
              onClick={() => start.mutate('apply')}
              aria-describedby={`${titleId}-apply`}
            >
              Apply and make redacted copy
            </Button>
            <p id={`${titleId}-apply`} className="text-xs text-ink-3">
              {applyHint(phase, pending, fileStop)}
            </p>
            <Button
              variant={
                (phase === 'stopped' && !fileStop) || phase === 'none' ? 'default' : 'outline'
              }
              className="w-full"
              disabled={busy || phase === 'working' || query.isPending}
              onClick={() => start.mutate('detect')}
            >
              {phase === 'none' ? 'Run detection' : 'Run detection again'}
            </Button>
            {actionError ? (
              <p role="alert" className="text-sm text-destructive">
                {actionError}
              </p>
            ) : null}
          </div>

          <div ref={listRef}>
            <h2 id={listTitleId} className="text-sm font-semibold">
              Marks{' '}
              <span className="font-normal text-ink-3">
                · {marks.filter((m) => markState(m.decision) === 'hidden').length} hidden
                {pending > 0 ? ` · ${pending} to decide` : ''}
              </span>
            </h2>
            {query.isPending ? (
              <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
            ) : marks.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {phase === 'working'
                  ? 'Marks appear here once detection finishes.'
                  : fileStop
                    ? 'The file wasn’t read, so nothing has been looked for yet.'
                    : drawable && editable
                      ? 'Nothing was found to hide. Draw a box on the page if something should be.'
                      : 'Nothing was found to hide.'}
              </p>
            ) : (
              <MarksList
                labelledBy={listTitleId}
                marks={marks}
                selected={selected}
                disabled={!editable || busy}
                onSelect={(id) => select(id, 'list')}
                onDecide={(items) => decide.mutate(items)}
              />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function BackLink({ roomId }: { roomId: string }) {
  return (
    <Link
      to={`/rooms/${roomId}/processing`}
      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
    >
      <Icon name="arrowLeft" size={14} />
      Processing status
    </Link>
  );
}

/** The approve button's confirm line: what approving does to THIS document. */
function approveHint(phase: ReviewPhase, pending: number, marks: number): string {
  switch (phase) {
    case 'decide':
      return `Approves the marks as shown: the ${pending} undecided ${pending === 1 ? 'item is' : 'items are'} hidden too, then the redacted copy is made.`;
    case 'apply':
      return 'Approves your decisions and makes the redacted copy with them.';
    case 'done':
      return marks === 0
        ? 'Nothing was found to hide. Approving records that you checked it.'
        : 'Records that you checked it. The redacted copy stays as it is.';
    case 'stopped':
      return 'There is no redacted copy to approve until the copy can be made.';
    case 'working':
      return 'Working…';
    case 'unsupported':
      return 'This file type can’t be redacted automatically.';
    case 'none':
      return 'Run detection first.';
  }
}

function applyHint(phase: ReviewPhase, pending: number, fileStop = false): string {
  switch (phase) {
    case 'decide':
      return `${pending} ${pending === 1 ? 'item still needs' : 'items still need'} a decision — choose Hide or Keep visible.`;
    case 'apply':
      return 'Makes the redacted copy with your decisions. It is checked before anyone sees it.';
    case 'done':
      return 'The redacted copy already has every decision.';
    case 'stopped':
      return fileStop
        ? 'Upload a new version of the file to make a redacted copy.'
        : 'The last attempt stopped — run detection again first. Your decisions and boxes are kept.';
    case 'working':
      return 'Working…';
    case 'unsupported':
      return 'This file type can’t be redacted automatically.';
    case 'none':
      return 'Run detection first.';
  }
}

function StatusBanner({
  data,
  phase,
  pending,
  loading,
  made,
  requested,
  onNext,
  walking,
  drawable,
  fileName,
}: {
  data: DocumentRedaction | undefined;
  phase: ReviewPhase;
  pending: number;
  loading: boolean;
  made: boolean;
  requested: 'apply' | 'detect' | null;
  onNext: () => void;
  walking: boolean;
  /** The original is shown as pages that can be drawn on. */
  drawable: boolean;
  fileName: string | null;
}) {
  if (loading) return null;
  const run = data?.run;
  const box = (tone: 'muted' | 'check' | 'error' | 'done', children: ReactNode, alert = false) => (
    <div
      role={alert ? 'alert' : 'status'}
      data-testid="redaction-status"
      data-tone={tone}
      className={cn(
        'rounded-lg border p-3 text-sm',
        tone === 'check' && 'border-st-check-line bg-st-check text-st-check-fg',
        tone === 'error' && 'border-st-error-line bg-st-error text-st-error-fg',
        tone === 'done' && 'border-st-filed-line bg-st-filed text-st-filed-fg',
        tone === 'muted' && 'bg-surface text-ink-2',
      )}
    >
      {children}
    </div>
  );
  switch (phase) {
    case 'none':
      return box(
        'muted',
        'Redaction hasn’t run on this document yet. Run detection to look for personal data.',
      );
    case 'working': {
      const applying = run?.status === 'applying' || run?.phase === 'apply';
      const progress = run ? runProgress(run) : null;
      return box(
        'muted',
        <span className="inline-flex items-center gap-2">
          <Icon name="spinner" size={14} className="animate-spin" />
          {applying ? 'Making the redacted copy and checking it' : 'Looking for personal data'}
          {progress ? ` — ${progress}` : ''}…
        </span>,
      );
    }
    case 'stopped':
      return box(
        run?.status === 'failed' ? 'error' : 'check',
        <>
          <p className="font-semibold">
            {requested === 'apply' || requested === 'detect'
              ? 'The redacted copy didn’t pass its check'
              : 'The redacted copy couldn’t be made'}
          </p>
          <p className="mt-1">{run ? stopReason(run, fileName) : null}</p>
          {run && PAGE_FIXABLE.has(run.failureReason ?? '') && drawable ? (
            <p className="mt-1">
              Fix it here (draw a box, or change a decision), then choose Run detection again — your
              decisions are kept and the copy is made once detection finishes.
            </p>
          ) : null}
          <p className="mt-1">Buyers see “Being prepared” for this document until then.</p>
        </>,
        true,
      );
    case 'unsupported':
      return box(
        'muted',
        'This file type can’t be redacted automatically. Buyers see “Being prepared” for it.',
      );
    case 'decide':
      return box(
        'check',
        <>
          <p className="font-semibold">
            {pending} {pending === 1 ? 'item needs' : 'items need'} a decision
          </p>
          <p className="mt-1">
            Black boxes will be hidden in the redacted copy; striped boxes wait for you. Choose Hide
            or Keep visible for each, then Apply. Buyers see “Being prepared” until then.
          </p>
        </>,
      );
    case 'apply':
      return box(
        'check',
        'Every item is decided. Choose Apply and make redacted copy — buyers see “Being prepared” until it is made.',
      );
    case 'done':
      return box(
        'done',
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>
            {made
              ? 'Redacted copy made and checked — this is the copy everyone is served.'
              : 'The redacted copy is up to date with every decision.'}
            {approvalLine(run) ? ` ${approvalLine(run)}` : ''}
          </span>
          <Button size="sm" onClick={onNext} disabled={walking}>
            Next document
          </Button>
        </div>,
      );
  }
}

/** Failures a person fixes on the page itself: a box over what is still readable. */
const PAGE_FIXABLE: ReadonlySet<string> = new Set([
  'verify_failed',
  'ocr_failed',
  'page_unrenderable',
]);

/** How the original is shown: pages that can be drawn on, a Word / Excel preview, or not yet. */
type Surface = 'drawable' | 'office' | null;

function Legend() {
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2" aria-label="Key">
      <li className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className="inline-block h-3 w-4 bg-black" />
        Will be hidden
      </li>
      <li className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className={cn('inline-block h-3 w-4', UNDECIDED_BOX)}
          style={STRIPES}
        />
        Needs a decision
      </li>
      <li className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className={cn('inline-block h-3 w-4', VISIBLE_BOX)} />
        Kept visible
      </li>
    </ul>
  );
}

const UNDECIDED_BOX = 'border-2 border-st-check-fg';
const VISIBLE_BOX = 'border border-dashed border-ink-3';
const STRIPES = {
  backgroundImage:
    'repeating-linear-gradient(45deg, rgb(var(--st-check-fg) / 0.35) 0 4px, transparent 4px 8px)',
};

/** "Original with marks" (where the review happens) | "Redacted copy" (what everyone is served). */
function ViewSwitch({ value, onChange }: { value: Viewed; onChange: (next: Viewed) => void }) {
  const options: { value: Viewed; label: string }[] = [
    { value: 'original', label: 'Original with marks' },
    { value: 'buyer', label: 'Redacted copy' },
  ];
  return (
    <div
      role="group"
      aria-label="Which copy to view"
      className="inline-flex rounded-md border border-input p-0.5"
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'h-7 rounded px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-tap',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** The redacted copy, or a plain statement of why there is none yet. */
function BuyerCopy({
  roomId,
  documentId,
  data,
  title,
}: {
  roomId: string;
  documentId: string;
  data: DocumentRedaction | undefined;
  title: string;
}) {
  // The same rule the data-room viewer uses (lib/useBuyerCopy viewerPlan): the
  // redacted copy once redaction is complete, otherwise why buyers have none.
  const plan = viewerPlan({ redactionStatus: viewerRedactionStatus(data), paperworkKind: null });
  const state = useRedactedCopy(roomId, documentId, plan.kind === 'redacted');
  const notice =
    plan.kind === 'held'
      ? plan.notice
      : plan.kind === 'original'
        ? { title: 'Redaction is off for this room', detail: 'Buyers see the original.' }
        : state.kind === 'none'
          ? {
              title: 'No redaction needed — buyers see the original',
              detail: 'The redaction check found nothing that needed hiding in this document.',
            }
          : null;
  if (!notice && state.kind === 'pdf') {
    return (
      <PdfViewer data={state.data} title={`${title} (redacted copy)`} className="min-h-0 flex-1" />
    );
  }
  if (!notice && state.kind === 'image') {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/30 p-4">
        <img src={state.url} alt={`${title} (redacted copy)`} className="max-h-full max-w-full" />
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/30 p-4">
      <div className="max-w-md text-center" data-testid="buyer-copy-notice">
        {notice ? (
          <>
            <p className="text-sm font-medium">{notice.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{notice.detail}</p>
          </>
        ) : state.kind === 'loading' || state.kind === 'idle' ? (
          <p className="text-sm text-muted-foreground">Loading redacted copy…</p>
        ) : state.kind === 'error' ? (
          <p className="text-sm text-destructive">{state.message}</p>
        ) : state.kind === 'file' ? (
          <>
            <p className="text-sm font-medium">The redacted copy is ready</p>
            <p className="mt-1 text-sm text-muted-foreground">
              It is a{' '}
              {state.extension === 'xlsx'
                ? 'spreadsheet'
                : state.extension === 'tif'
                  ? 'TIFF image'
                  : 'Word document'}
              , which can’t be shown here.{' '}
              <a href={state.url} download className="font-medium text-primary hover:underline">
                Download it to check
              </a>
              .
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

interface MarksOnPageProps {
  marks: RedactionMarkView[];
  selected: string | null;
  onSelect: (markId: string) => void;
  canDraw: boolean;
  drawTouch: boolean;
  onDraw: (pageNumber: number, box: RedactionBox) => void;
  /** A touch or pen drag arrived while drawing with them is off: offer the switch. */
  onTouch: () => void;
}

/** The original, every page with its marks over it. */
function OriginalWithMarks({
  roomId,
  documentId,
  name,
  onSurface,
  ...marksProps
}: MarksOnPageProps & {
  roomId: string;
  documentId: string;
  name: string;
  onSurface: (surface: Surface) => void;
}) {
  const { content, error, loading } = useDocumentPreview(roomId, documentId, name);
  const shown: Surface =
    (content?.kind === 'pdf' && content.data) || content?.kind === 'image'
      ? 'drawable'
      : content?.kind === 'office'
        ? 'office'
        : null;
  useEffect(() => {
    onSurface(shown);
  }, [shown, onSurface]);
  const markCount = marksProps.marks.length;
  const undecided = undecidedCount(marksProps.marks);
  const layer = (pageNumber: number) => (
    <PageMarks
      pageNumber={pageNumber}
      {...marksProps}
      marks={marksProps.marks.filter((m) => m.pageNumber === pageNumber)}
    />
  );
  if (content?.kind === 'pdf' && content.data) {
    return (
      <PdfViewer
        data={content.data}
        title={name}
        renderPageOverlay={layer}
        className="min-h-0 flex-1"
      />
    );
  }
  if (content?.kind === 'image') {
    return (
      <div className="min-h-0 flex-1 overflow-auto bg-muted/30 p-4">
        <div className="relative mx-auto w-fit" data-page={1}>
          <img src={content.url} alt={name} className="block max-w-full" draggable={false} />
          {layer(1)}
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {loading ? (
        <p className="p-6 text-sm text-muted-foreground">Loading document…</p>
      ) : error ? (
        <p className="p-6 text-sm text-destructive">{error}</p>
      ) : (
        <>
          {markCount > 0 ? (
            <p
              className="border-b bg-surface px-3 py-2 text-xs text-ink-2"
              data-testid="office-marks-hint"
            >
              Word and Excel files are redacted inside the file, so the marks are listed beside this
              preview instead of drawn on it
              {undecided > 0 ? ' — decide each one in the list.' : '.'}
            </p>
          ) : null}
          {content?.kind === 'office' &&
          content.preview &&
          content.preview.kind !== 'unsupported' ? (
            <DocumentPreviewView
              preview={content.preview}
              title={name}
              className="min-h-0 flex-1"
            />
          ) : null}
        </>
      )}
    </div>
  );
}

const pct = (v: number) => `${(v * 100).toFixed(4)}%`;
/** Smallest drag (CSS px) that counts as a box rather than a click. */
const MIN_DRAG_PX = 4;

/**
 * One page's marks, positioned in the page's own 0..1 coordinates (the API's:
 * the upright page from its top-left corner), and the drawing surface: a drag
 * with the mouse (or with touch or a pen, once "Draw with touch or pen" is on —
 * off, the browser takes that drag as a scroll) adds a box.
 * Hidden from assistive tech — the marks list is the accessible way to the same
 * marks; drawing is pointer-only.
 */
function PageMarks({
  pageNumber,
  marks,
  selected,
  onSelect,
  canDraw,
  drawTouch,
  onDraw,
  onTouch,
}: MarksOnPageProps & { pageNumber: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  const at = (e: ReactPointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const x = r.width > 0 ? (e.clientX - r.left) / r.width : 0;
    const y = r.height > 0 ? (e.clientY - r.top) / r.height : 0;
    return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)), r };
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!canDraw || e.button !== 0) return;
    if (e.pointerType !== 'mouse' && !drawTouch) {
      // The browser scrolls with this drag; the switch to draw with it is shown.
      onTouch();
      return;
    }
    if ((e.target as HTMLElement).closest('[data-mark-box]')) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // Capture is a nicety (a drag that leaves the page keeps tracking).
    }
    const { x, y } = at(e);
    setDrag({ x0: x, y0: y, x1: x, y1: y });
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const { x, y } = at(e);
    setDrag({ ...drag, x1: x, y1: y });
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const { x, y, r } = at(e);
    setDrag(null);
    const wide = Math.abs(x - drag.x0) * r.width >= MIN_DRAG_PX;
    const tall = Math.abs(y - drag.y0) * r.height >= MIN_DRAG_PX;
    const box = wide && tall ? boxFromDrag(drag.x0, drag.y0, x, y) : null;
    if (box) onDraw(pageNumber, box);
  };

  const preview = drag ? boxFromDrag(drag.x0, drag.y0, drag.x1, drag.y1) : null;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-page-marks={pageNumber}
      className={cn(
        'absolute inset-0',
        canDraw && 'cursor-crosshair',
        canDraw && drawTouch && 'touch-none',
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => setDrag(null)}
    >
      {marks.flatMap((m) => {
        const state = markState(m.decision);
        // A drawn box the person removed is gone from the page.
        if (m.source === 'manual' && state !== 'hidden') return [];
        const isSelected = selected === m.markId;
        return m.boxes.map((b, i) => (
          <div
            key={`${m.markId}-${i}`}
            data-mark-box={m.markId}
            data-state={state}
            title={markKind(m)}
            onClick={() => onSelect(m.markId)}
            className={cn(
              'absolute cursor-pointer',
              state === 'hidden' && 'bg-black',
              state === 'hidden' && isSelected && 'opacity-60',
              state === 'undecided' && UNDECIDED_BOX,
              state === 'visible' && VISIBLE_BOX,
              isSelected && 'z-10 ring-2 ring-primary ring-offset-1',
            )}
            style={{
              left: pct(b.x),
              top: pct(b.y),
              width: pct(b.w),
              height: pct(b.h),
              ...(state === 'undecided' ? STRIPES : {}),
            }}
          />
        ));
      })}
      {preview ? (
        <div
          data-testid="draw-preview"
          className="absolute border-2 border-primary bg-primary/15"
          style={{
            left: pct(preview.x),
            top: pct(preview.y),
            width: pct(preview.w),
            height: pct(preview.h),
          }}
        />
      ) : null}
    </div>
  );
}

/** The marks by page, each with Hide / Keep visible (or Remove for a drawn box). */
function MarksList({
  labelledBy,
  marks,
  selected,
  disabled,
  onSelect,
  onDecide,
}: {
  labelledBy: string;
  marks: RedactionMarkView[];
  selected: string | null;
  disabled: boolean;
  onSelect: (markId: string) => void;
  onDecide: (items: { markId: string; decision: 'approved' | 'rejected' }[]) => void;
}) {
  return (
    <div className="mt-2 space-y-4" aria-labelledby={labelledBy} role="group">
      {marksByPage(marks).map((g) => {
        // "Hide all": the found marks not hidden yet (a drawn box you removed stays removed).
        const toHide = g.marks.filter(
          (m) => m.source !== 'manual' && markState(m.decision) !== 'hidden',
        );
        return (
          <section key={g.pageNumber} aria-label={`Page ${g.pageNumber}`}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-3">
                Page {g.pageNumber}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled || toHide.length === 0}
                onClick={() =>
                  onDecide(toHide.map((m) => ({ markId: m.markId, decision: 'approved' })))
                }
              >
                Hide all on this page
              </Button>
            </div>
            <ul className="mt-1 space-y-2">
              {g.marks.map((m) => (
                <MarkItem
                  key={m.markId}
                  mark={m}
                  selected={selected === m.markId}
                  disabled={disabled}
                  onSelect={() => onSelect(m.markId)}
                  onDecide={(decision) => onDecide([{ markId: m.markId, decision }])}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function MarkItem({
  mark,
  selected,
  disabled,
  onSelect,
  onDecide,
}: {
  mark: RedactionMarkView;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onDecide: (decision: 'approved' | 'rejected') => void;
}) {
  const kind = markKind(mark);
  const masked = maskedPreview(mark);
  const last = masked ? (masked.match(/\d/g) ?? []).join('') : '';
  const state = markState(mark.decision);
  const manual = mark.source === 'manual';
  const status = manual
    ? state === 'hidden'
      ? 'Will be hidden'
      : 'Removed'
    : state === 'hidden'
      ? mark.decision === 'auto'
        ? 'Will be hidden (found automatically)'
        : 'Will be hidden'
      : state === 'undecided'
        ? 'Needs a decision'
        : mark.decision === 'dismissed'
          ? 'Kept visible — judged not personal'
          : 'Kept visible';
  const name = `${kind}${last ? ` ending ${last}` : ''}, page ${mark.pageNumber}`;
  return (
    <li
      data-mark-item={mark.markId}
      data-state={state}
      className={cn(
        'rounded-md border bg-background p-2',
        state === 'undecided' && 'border-st-check-line',
        selected && 'border-primary ring-1 ring-primary',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className="block w-full rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-sm font-medium">{kind}</span>
        {masked ? (
          <>
            {' '}
            <span aria-hidden="true" className="font-mono text-sm text-ink-2">
              {masked}
            </span>
            <span className="sr-only"> ending {last}</span>
          </>
        ) : null}
        <span
          className={cn(
            'block text-xs',
            state === 'undecided' ? 'font-semibold text-st-check-fg' : 'text-ink-3',
          )}
        >
          {status}
          <span className="sr-only"> — show on the page</span>
        </span>
      </button>
      <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={`Decision for ${name}`}>
        {manual ? (
          state === 'hidden' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => onDecide('rejected')}
            >
              Remove box
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => onDecide('approved')}
            >
              Put box back
            </Button>
          )
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                state === 'hidden' &&
                  'border-inverse bg-inverse text-inverse-fg hover:bg-inverse/90 hover:text-inverse-fg',
              )}
              aria-pressed={state === 'hidden'}
              disabled={disabled}
              onClick={() => state !== 'hidden' && onDecide('approved')}
            >
              Hide
            </Button>
            <Button
              variant={state === 'visible' ? 'secondary' : 'outline'}
              size="sm"
              aria-pressed={state === 'visible'}
              disabled={disabled}
              onClick={() => state !== 'visible' && onDecide('rejected')}
            >
              Keep visible
            </Button>
          </>
        )}
      </div>
    </li>
  );
}
